import type { AdWritePath, DeskIdentity } from '../../api/settingsAutonomy'
import { post, useMsg } from './AutonomyPage'

/**
 * Blocul "Identity: Active Directory" din Service desk (F12): drumul de scriere
 * in AD si DC-ul tinta se salveaza cu Save-ul panoului (`POST .../desk`);
 * "Check the DC" si "I reviewed the delegations" sunt scrieri separate.
 */
const PATHS: [AdWritePath, string, string][] = [
  ['none', 'off', 'domain accounts go to a person'],
  ['dc_runbook', 'through the domain controller agent', "fixed PowerShell templates, code on the person's screen first"],
  ['connector', 'through the directory connector', 'not available yet'],
]

const ITEM_LABEL: Record<string, string> = {
  write_path_set: 'write path set',
  agent_present: 'agent on the domain controller',
  agent_version: 'agent version',
  snapshot_fresh: 'AD snapshot',
  dc_writable: 'writable DC (Check the DC)',
}

function shown(v: unknown): string {
  return v === null || v === undefined || v === '' ? '—' : String(v)
}

export function AutonomyIdentityBlock({ identity, path, dcId, isGlobal, onPath, onDc, onChange }: {
  identity: DeskIdentity
  path: AdWritePath
  dcId: number
  isGlobal: boolean
  onPath(p: AdWritePath): void
  onDc(id: number): void
  onChange(): void
}) {
  const [msg, say] = useMsg()
  if (identity.per_company) {
    return (
      <div id="odsk-identity" style={{ marginTop: 10 }}>
        <strong>Identity: Active Directory</strong>
        <div className="text-muted" style={{ marginTop: 4 }}>Set per company: open a company to choose how the desk may write to Active Directory.</div>
      </div>
    )
  }
  const items = identity.prerequisites?.items ?? []
  const target = dcId > 0 ? identity.dcs.find((d) => d.device_id === dcId) : identity.dc
  const checkDc = () => post('/desk/dc-check', { device_id: String(target?.device_id ?? 0) }, say, () => setTimeout(onChange, 600), 'Check queued; the answer shows on the next refresh')

  return (
    <div id="odsk-identity" style={{ marginTop: 10 }}>
      <strong>Identity: Active Directory</strong>
      {PATHS.map(([value, label, hint]) => (
        <label key={value} style={{ display: 'block', marginTop: 4, marginLeft: 18 }}>
          <input type="radio" name="odsk-ad-path" value={value} checked={path === value} onChange={() => onPath(value)} /> <strong>{label}</strong> ({hint})
        </label>
      ))}
      {path === 'connector' && (
        <div id="odsk-ad-connector" style={{ marginTop: 4, marginLeft: 18, color: '#b54708' }}>
          The directory connector is not available yet: the desk treats this as off and domain accounts still go to a person.
        </div>
      )}
      <div style={{ marginTop: 6, marginLeft: 18 }}>
        <label htmlFor="odsk-ad-dc" style={{ fontWeight: 'normal' }}>Identity target DC</label>{' '}
        <select id="odsk-ad-dc" className="form-control input-sm" style={{ display: 'inline-block', width: 'auto' }} value={String(dcId)} onChange={(e) => onDc(parseInt(e.target.value, 10) || 0)}>
          <option value="0">newest snapshot{identity.dc && dcId === 0 ? ` (${identity.dc.name})` : ''}</option>
          {identity.dcs.map((d) => (
            <option key={d.device_id} value={String(d.device_id)}>
              {d.name} · agent {shown(d.agent_version)} · AD read {shown(d.collected_at)}
            </option>
          ))}
        </select>
      </div>
      <ul id="odsk-ad-prereq" style={{ listStyle: 'none', paddingLeft: 18, marginTop: 6, marginBottom: 0 }}>
        {items.map((it) => (
          <li key={it.key} data-key={it.key} data-ok={it.ok ? '1' : '0'} style={{ color: it.ok ? '#2e7d32' : '#b42318' }}>
            {it.ok ? '✓' : '✗'} {ITEM_LABEL[it.key] ?? it.key}: {shown(it.value)}
            {!it.ok && it.hint ? <span className="text-muted"> — {it.hint}</span> : null}
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 6, marginLeft: 18, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-default btn-xs" id="odsk-dc-check" disabled={!target} onClick={checkDc}>Check the DC</button>
        {isGlobal && (
          <button type="button" className="btn btn-default btn-xs" id="odsk-delegations-reviewed"
            onClick={() => post('/desk/delegations-reviewed', {}, say, () => setTimeout(onChange, 600), 'Noted')}>
            I reviewed the delegations
          </button>
        )}
        <span className="text-muted">
          delegations {identity.delegations_reviewed_at ? `reviewed ${identity.delegations_reviewed_at}` : 'not reviewed yet'} (Settings &rarr; Contacts &rarr; Delegations)
        </span>
        {msg}
      </div>
      <div style={{ marginTop: 8, marginLeft: 18 }}><strong>Verified unknown senders</strong> <span className="text-muted">(returned the one-time code; served read-only)</span></div>
      {identity.unknown_verified.length === 0 ? (
        <div className="text-muted" style={{ marginLeft: 18 }}>none</div>
      ) : (
        <table id="odsk-unknown-verified" className="table table-condensed" style={{ fontSize: 12, marginTop: 4, marginLeft: 18, width: 'auto' }}>
          <thead><tr><th>Address</th><th>Last message</th><th>Messages</th><th /></tr></thead>
          <tbody>
            {identity.unknown_verified.map((u) => (
              <tr key={u.from_address}>
                <td>{u.from_address}</td>
                <td>{u.last_seen}</td>
                <td>{u.messages}</td>
                <td><a href={`/app/settings/contacts?email=${encodeURIComponent(u.from_address)}`}>Make a contact</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
